import { Duration, endOfWeek, format, getWeekOfMonth, parse } from 'date-fns';
import { useTranslation } from 'react-i18next';

import type { ChargerStatus } from 'clipsal-cortex-types/src/api/api-evcharger';

import { DateRangeType } from '../../common/components/DateRangeTypePicker';
import { getTimeFormat } from '../account/settings/settings-helpers';
import { ChargingActivitySessionData, QRCodeValues } from './types';

// Statuses reported via SSE status update events when the charger cannot be reached.
// Every other status (incl. Charging and Faulted) can only be reported by a connected charger.
export const OFFLINE_EV_CHARGER_STATUSES: ChargerStatus[] = [
  'Unavailable_Disconnect',
  'Unavailable_Restart',
  'Unavailable_Charger',
];

export function isEvChargerOffline(status?: ChargerStatus): boolean {
  return !status || OFFLINE_EV_CHARGER_STATUSES.includes(status);
}

function getSessionTimeRangeLabel(start: Date, end: Date): string {
  const timeFormat = getTimeFormat();

  if (timeFormat === '24HR') {
    return `${format(start, 'HH:mm')}-${format(end, 'HH:mm')}`;
  }

  return `${format(start, 'h:mm')}-${format(end, 'h:mm aa')}`;
}

function getSnFromSsid(ssid: string): string {
  const parts = ssid.split('_');
  return parts[parts.length - 1] || '';
}

export function parseQRCode(content: string): QRCodeValues | null {
  // QR code content sample:
  // WIFI:S:SECharge_123;T:WPA;P:pw_for_WPA-PSK_or_WPA2-PSK;H:FALSE;CPID=11a2222-3333-4444-5555-66bc77b888d9;
  if (!content) {
    return null;
  }

  content = content.replace('WIFI:', '');
  content = content.replace(/CPID=/i, 'CPID:');

  // Split by unescaped semicolons.
  const splittedChargerProperties = content.split(/(?<!\\);/);

  const codeValues = splittedChargerProperties.reduce(
    (acc, curr) => {
      const [key, ...valueParts] = curr.split(':');
      let value = valueParts.join(':'); // Rejoin in case value contains colons.
      if (['S', 'P', 'CPID'].includes(key.toUpperCase())) {
        // Unescape special characters.
        value = value
          .replace(/\\:/g, ':')
          .replace(/\\;/g, ';')
          .replace(/\\,/g, ',')
          .replace(/\\"/g, '"')
          .replace(/\\\\/g, '\\')
          .trim();

        return { ...acc, [key.toUpperCase()]: value };
      }
      return acc;
    },
    { S: '', P: '', CPID: '' }
  );

  return {
    cpid: codeValues.CPID,
    sn: getSnFromSsid(codeValues.S),
    wifi:
      !codeValues.S || !codeValues.P
        ? null
        : {
            ssid: codeValues.S,
            password: codeValues.P,
          },
  };
}

export function getTestingQRCodeContent(
  useInvalidData = false,
  useVirtualDevice = false,
  useInvalidCpid = false
): string {
  const cpid = useInvalidCpid ? 'not-a-valid-uuid-format' : '0586996e-3d14-403d-9b71-31012010e7f2';
  const sn = useInvalidData ? '' : 'EVB1A33P4KI3N214912002400150027AZ';
  const wifiSsid = useInvalidData ? '' : useVirtualDevice ? `Virtual_${sn}` : `SECharge_${sn}`;
  const wifiPassword = 'fake_password';
  return `WIFI:S:${wifiSsid};T:WPA;P:${wifiPassword};H:false;cpid=${cpid};`;
}

/**
 * Formats seconds into a string representing the total time in "H:MM" format.
 * The days are converted to hours and added to the hours property.
 *
 * @param sessionTime - The total session time in seconds.
 * @returns A string representing the total time in hours and minutes.
 */
export function formatTotalTime(sessionTime: number): string {
  const duration: Duration = {
    hours: Math.floor(sessionTime / 3600),
    minutes: Math.floor((sessionTime % 3600) / 60),
    seconds: sessionTime % 60,
  };

  const days = duration.days || 0;
  const hours = duration.hours || 0;
  const minutes = duration.minutes || 0;

  return `${days * 24 + hours}:${minutes < 10 ? '0' + minutes : minutes}`;
}

export function getDateRangeTypeLabel(
  dateRangeType: DateRangeType,
  start: string,
  chargingSessionId: number,
  weekStartsOn: 0 | 1 | 2 | 3 | 4 | 5 | 6 = 0
) {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { t } = useTranslation();

  if (!start) return '';

  switch (dateRangeType) {
    case DateRangeType.Day:
      return `${t('Common.session')} ${chargingSessionId}`;

    case DateRangeType.Week:
      return format(start, 'EEEE');

    case DateRangeType.Month:
      return `${t('Common.week')} ${getWeekOfMonth(new Date(start), { weekStartsOn })}`;

    case DateRangeType.Year:
      return format(start, 'LLLL');

    default:
      return '';
  }
}

/**
 * Generates an ARIA label for a given date range type and start/end dates.
 *
 * @param dateRangeType - The type of date range (Day, Week, Month, Year).
 * @param start - The start date as a string.
 * @param end - The end date as a string.
 * @returns A formatted ARIA label string based on the date range type:
 * if DAY = Mon, 8/24/24, 2:00-7:30pm
 * if WEEK = 4/4/24, 12:00-6:00am
 * if MONTH = 4/1/25 - 4/5/25
 * if YEAR = 1/1/25 -1/31/25
 */
export function getDateRangeTypeAriaLabel(
  dateRangeType: DateRangeType,
  start: string,
  end: string,
  weekStartsOn: 0 | 1 | 2 | 3 | 4 | 5 | 6 = 0
): string {
  if (!start || !end) return '';

  // there is inconsistency for start and end format
  // with this we are assuring that UI is getting always same format
  // yyyy-MM-dd'T'HH:mm:ss
  const startTrimmed = start.slice(0, 19);
  const endTrimmed = end.slice(0, 19);
  const weekEnd = endOfWeek(start, { weekStartsOn });
  const localStartDate = parse(startTrimmed, "yyyy-MM-dd'T'HH:mm:ss", new Date());
  const localEndDate = parse(endTrimmed, "yyyy-MM-dd'T'HH:mm:ss", new Date());

  switch (dateRangeType) {
    case DateRangeType.Day:
      return `${format(start, 'EE, M/d/yy')}, ${getSessionTimeRangeLabel(localStartDate, localEndDate)}`;

    case DateRangeType.Week:
      return `${format(start, 'M/d/yy')}, ${getSessionTimeRangeLabel(localStartDate, localEndDate)}`;

    case DateRangeType.Month:
      return `${format(start, 'M/d/yy')} - ${format(weekEnd, 'M/d/yy')}`;

    case DateRangeType.Year:
      return `${format(start, 'M/d/yy')} - ${format(end, 'M/d/yy')}`;

    default:
      return '';
  }
}

export function formatSecondsToHHMM(totalSeconds: number): string {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  if (!minutes) {
    return `${hours}`;
  }
  return `${hours}:${String(minutes).padStart(2, '0')}`;
}

export function groupByWeeks(
  records: ChargingActivitySessionData[],
  weekStartsOn: 0 | 1 | 2 | 3 | 4 | 5 | 6 = 0
): ChargingActivitySessionData[] {
  const weeks: Record<number, ChargingActivitySessionData> = {};

  for (const r of records) {
    if (!r.start) continue;

    const id = getWeekOfMonth(new Date(r.start), { weekStartsOn });
    if (!weeks[id]) {
      weeks[id] = {
        id,
        start: r.start,
        end: r.end,
        usage: 0,
        cost: 0,
        records: [...r.records],
      };
    }

    weeks[id].usage += r.usage;
    weeks[id].cost += r.cost;
    weeks[id].records.push(...r.records);

    // update start/end boundaries
    const currentStart = new Date(weeks[id].start);
    const currentEnd = new Date(weeks[id].end);
    const recordStart = new Date(r.start);
    const recordEnd = new Date(r.end);
    if (recordStart < currentStart) weeks[id].start = r.start;
    if (recordEnd > currentEnd) weeks[id].end = r.end;
  }

  // sort by week number
  return Object.values(weeks).sort((a, b) => a.id - b.id);
}
